import { useEffect, useRef, useMemo, useState } from 'react';
import { SiteVideoData } from '../../../interfaces';

interface UseVerticalVideoPlayerProps {
    itemId: string;
    index: number;
    media: SiteVideoData;
    isGalleryView: boolean;
    showPrerollAds: boolean;
    hidePrerollAds?: boolean;
    prerollIndex?: number;
    currentPlayingVideo?: number;
    onNextSlide?: () => void;
    enabled?: boolean;
}

export const useVerticalVideoPlayer = (props: UseVerticalVideoPlayerProps) => {
    const { itemId, index, media, isGalleryView, showPrerollAds, hidePrerollAds, currentPlayingVideo, onNextSlide, enabled = true } = props;

    const [playerMountKey, setPlayerMountKey] = useState(0);
    const playerApiRef: any = useRef<any>(undefined);
    const wasActiveRef: { current: boolean } = useRef<boolean>(isGalleryView && currentPlayingVideo !== undefined ? currentPlayingVideo === index : true);
    const observerRef: { current: MutationObserver | null } = useRef<MutationObserver>(null);
    const observeRetryTimerRef: { current: number | undefined } = useRef<number | undefined>(undefined);
    const observeTimeoutRef: { current: number | undefined } = useRef<number | undefined>(undefined);

    const hasPrerollAds = enabled && !!showPrerollAds && !hidePrerollAds;
    const isActive = isGalleryView && currentPlayingVideo !== undefined ? currentPlayingVideo === index : true;
    const playerMedia: SiteVideoData | null = useMemo(() => {
        if (!enabled || !media) return null;
        return { ...media, isAutoPlaying: isActive && !!(media as SiteVideoData).isAutoPlaying } as SiteVideoData;
    }, [media, enabled, isActive]);
    const shouldDisplayVideo = enabled && (isGalleryView && currentPlayingVideo !== undefined
        ? (currentPlayingVideo === index || currentPlayingVideo === index - 1 || currentPlayingVideo === index + 1)
        : true);

    const observeCleanup = () => {
        clearTimeout(observeRetryTimerRef.current);
        clearTimeout(observeTimeoutRef.current);
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
    };

    const moveVideoElements = (targetNode: HTMLElement): boolean => {
        const pairs = [
            { source: ".fp-elapsed", target: "#elapsed-target" },
            { source: ".fp-duration", target: "#duration-target" },
            { source: ".fp-timeline", target: "#timeline-target" },
            { source: ".fp-volume-control", target: "#volume-target" },
        ];

        let allHandled = true;

        for (const { source, target } of pairs) {
            const sourceEl = targetNode.querySelector(source);
            const targetEl = targetNode.querySelector(target);

            if (!targetEl) continue; // design doesn't use this element — skip
            if (!sourceEl) { allHandled = false; continue; } // source not rendered yet — wait
            if (targetEl.contains(sourceEl)) continue; // already moved

            targetEl.innerHTML = "";
            targetEl.appendChild(sourceEl);
        }

        return allHandled;
    };

    const observeAndMoveVideoElements = (retryCount = 0) => {
        const targetNode = document.getElementById("VerticalVideoItem_" + itemId);
        if (!targetNode) {
            if (retryCount < 20) { // retry up to 2 seconds
                observeRetryTimerRef.current = window.setTimeout(() => observeAndMoveVideoElements(retryCount + 1), 100);
            }
            return;
        }

        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        clearTimeout(observeTimeoutRef.current);

        const stopObserving = () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            clearTimeout(observeTimeoutRef.current);
        };

        const maxObserveTime = 10000;
        observeTimeoutRef.current = window.setTimeout(stopObserving, maxObserveTime);

        // If Flowplayer already rendered (common on the first/active slide), mutations
        // won't fire — move immediately, then keep observing for late/ad re-renders.
        if (moveVideoElements(targetNode)) {
            stopObserving();
            return;
        }

        observerRef.current = new MutationObserver(() => {
            if (moveVideoElements(targetNode)) {
                stopObserving();
            }
        });

        observerRef.current.observe(targetNode, {
            childList: true,
            subtree: true,
        });
    };

    const handleVideoEnded = (e) => {
        // On iOS, IMA uses the same video element for ads ("custom playback" mode).
        // When the ad finishes, "ended" fires before content starts — ignore it.
        const api = playerApiRef.current;
        if (api) {
            const container = api.root || api.el || (e.target && e.target.closest && e.target.closest(".flowplayer"));
            if (container && container.classList && (container.classList.contains("is-ad-linear") || container.classList.contains("ad-playing"))) {
                return;
            }
        }
        e.target.currentTime = 0;
        isGalleryView && onNextSlide ? onNextSlide() : e.target.click();
    };

    const onPlayerLoaded = (player) => {
        const p: any = player as any;
        const coerced = (p && typeof p.play === "function")
            ? p
            : (p && p.api && typeof p.api.play === "function")
                ? p.api
                : (p && p[0] && typeof p[0].play === "function")
                    ? p[0]
                    : p;

        playerApiRef.current = coerced;
        // Player chrome (elapsed/duration) often appears at load — ensure it gets relocated.
        observeAndMoveVideoElements();
    };

    // Initial mount: observe and move video elements
    useEffect(() => {
        if (!enabled) return;
        observeAndMoveVideoElements();

        return () => {
            observeCleanup();
        };
    }, [enabled]);

    // Re-observe when current playing video changes
    useEffect(() => {
        if (!enabled) return;
        if (currentPlayingVideo === index) {
            observeAndMoveVideoElements();
        }
        return () => {
            observeCleanup();
        };
    }, [currentPlayingVideo, enabled]);

    // Force player remount when transitioning from inactive to active
    // so FlowPlayer re-initializes with autoplay + IMA ads from scratch
    useEffect(() => {
        if (!enabled || !isGalleryView) return;
        if (isActive && !wasActiveRef.current) {
            setPlayerMountKey(playerMountKey + 1);
            playerApiRef.current = undefined;
        }
        wasActiveRef.current = isActive;
    }, [isActive, enabled, isGalleryView]);

    // Pause playback when the slide becomes inactive
    useEffect(() => {
        if (!enabled || !isGalleryView) return;
        if (!isActive) {
            const api = playerApiRef.current;
            if (api && typeof api.pause === "function") {
                try { api.pause(); } catch (e) { /* ignore */ }
            }
        }
    }, [isActive, enabled, isGalleryView]);

    return {
        playerMedia,
        isActive,
        isWaitingForAd: false,
        shouldDisplayVideo,
        hasPrerollAds,
        playerApiRef,
        playerMountKey,
        onPlayerLoaded,
        handleVideoEnded,
    };
};
